import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';
import classnames from 'clsx';
import { EditorState, RichUtils } from 'draft-js';

import { formatClassNames } from '@wix/editor-elements-common-utils';
import styles from '../button.scss';
import semanticClassNames from '../../../../RichTextBox.semanticClassNames';
import type { ExtendedToolbarChildrenProps } from '../../types';
import Tooltip from '../Tooltip';

export type InlineStyleButtonProps = ExtendedToolbarChildrenProps & {
  children: ReactNode;
  styleType: string;
  dataTestId?: string;
  tooltipText: string;
};

const InlineStyleButton = forwardRef<HTMLButtonElement, InlineStyleButtonProps>(
  (props, ref) => {
    const {
      styleType,
      children,
      setEditorState,
      getEditorState,
      dataTestId,
      lastSelectionBeforeKeyboardNavigation: lastSelection,
      shouldShowTooltips,
      tooltipText,
      tabIndex,
    } = props;

    const toggleStyle = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const isKeyboardEvent = event.detail === 0;
      if (isKeyboardEvent) {
        event.stopPropagation();
      }
      const refEditorState =
        lastSelection && isKeyboardEvent
          ? EditorState.forceSelection(getEditorState(), lastSelection)
          : getEditorState();
      setEditorState(RichUtils.toggleInlineStyle(refEditorState, styleType));
    };

    const preventBubblingUp = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    const selected =
      getEditorState().getCurrentInlineStyle().get(styleType) !== undefined;

    return (
      <div className={styles.buttonContainer}>
        <Tooltip shouldShow={shouldShowTooltips} content={tooltipText}>
          <button
            ref={ref}
            className={styles.button}
            onMouseDown={preventBubblingUp}
            onClick={toggleStyle}
            type="button"
            data-testid={dataTestId}
            aria-label={tooltipText}
            aria-pressed={selected ?? false}
            tabIndex={tabIndex}
          >
            <div
              className={classnames(
                styles.buttonContent,
                formatClassNames(semanticClassNames.icons),
                selected && styles.buttonContentSelected,
              )}
            >
              {children}
            </div>
          </button>
        </Tooltip>
      </div>
    );
  },
);

export default InlineStyleButton;
